import { useCallback, useEffect, useState } from "react";
import {
  MIN_STUDENTS_FOR_MEDIAN,
  MIN_TRIALS_FOR_CLASS_COMPARISON,
  type ClassCell,
  type ClassComparison,
} from "@talent/protocol";
import { ClassError, fetchComparison } from "../class-api.js";
import { COPY, fill, taskLabel, taskShortLabel } from "../copy.js";
import { fa } from "../format.js";
import { LevelBar } from "./level-bar.js";
import { useClasses } from "./class-context.js";
import { ScreenHeader } from "./screen-header.js";

/**
 * Students against each other — the thing the pivot was asked for, and the thing the old product
 * refused to build.
 *
 * It is buildable now because the comparison changed shape, not because the caution was dropped. A
 * teacher comparing children in their own class, on work they watched them do, is describing their
 * room. Placing a child on a national distribution that does not exist is a fabricated number, and
 * that remains unsayable (ADR-011).
 *
 * Two things are therefore on the screen and not in a comment:
 *
 * **The scope, stated on every load.** "Only among the children in this class" is the sentence that
 * makes the rest of it honest, and it is not shortenable.
 *
 * **The trial floor, and why.** Every ladder starts everyone at the bottom, so one visit to a game is
 * indistinguishable from struggling at it. A teacher who cannot see why a child is missing from a
 * column will read the gap as the child having failed.
 *
 * **The layout was rebuilt, and the reason is arithmetic.** It used to be one section per game, each
 * listing the whole class: a room of twenty children playing eight games is a hundred and sixty rows
 * and a hundred and sixty progress bars, so the screen got worse as a class got bigger. Worse, each
 * section was sorted on its own, so one child sat in a different place in every one of them and
 * "where is this child" had no answer. It is a grid now — children down, games across — which is the
 * shape of every mark book a teacher has ever read, and the shape in which a row is one child.
 */

const CARD = "rounded-lg bg-card px-md py-md shadow-card";

type State =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "ready"; comparison: ClassComparison };

/** Where a child's level sits against the class median, or why it cannot be said. */
type Standing = "above" | "at" | "below" | "thin" | "none";

/**
 * Reading one cell, with the two refusals kept together.
 *
 * A number the child reached is always a fact and always drawn. Whether it sits above or below the
 * middle of the room is an inference, and it is refused twice: once when the child has too few
 * turns for their own level to mean anything, and once when the median rests on too few children to
 * be a middle at all. Both refusals produce a number with no tint rather than an absence — a blank
 * where a child has played is the thing a teacher reads as failure.
 */
function standingOf(
  cell: ClassCell | undefined,
  median: number | null,
  from: number,
): Standing {
  if (!cell) return "none";
  if (cell.trials < MIN_TRIALS_FOR_CLASS_COMPARISON) return "thin";
  if (median === null || from < MIN_STUDENTS_FOR_MEDIAN) return "thin";
  if (cell.reached > median) return "above";
  if (cell.reached < median) return "below";
  return "at";
}

const CELL_LOOK: Record<Standing, string> = {
  above: "bg-correct/22 text-ink",
  at: "bg-edge text-ink",
  below: "bg-wrong/18 text-ink",
  thin: "text-muted",
  none: "text-edge",
};

export function CompareScreen({
  onOpenStudent,
  onBack,
  onSignedOut,
}: {
  onOpenStudent: (studentId: string) => void;
  onBack: () => void;
  onSignedOut: () => void;
}) {
  const { current } = useClasses();
  const classId = current?.classId ?? null;
  const [state, setState] = useState<State>({ kind: "loading" });
  const [openGame, setOpenGame] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!classId) return;
    try {
      setState({ kind: "ready", comparison: await fetchComparison(classId) });
    } catch (err) {
      if (err instanceof ClassError && err.failure === "unauthenticated") {
        onSignedOut();
        return;
      }
      setState({
        kind: "failed",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [classId, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The header is drawn before the data is, on every branch.
   *
   * It used to be inside the ready branch only, so a class with nothing to compare yet — the ordinary
   * state of a new class — rendered a bare sentence with no title and no way back. A screen you can
   * reach and cannot leave is worse than a screen with nothing on it.
   */
  const header = (
    <ScreenHeader
      title={COPY.teacher.compare.title}
      onBack={onBack}
      backLabel={COPY.teacher.compare.back}
    />
  );
  const framed = (body: React.ReactNode) => (
    <div className="mx-auto min-h-full max-w-[720px] px-lg pt-lg pb-xxl">
      {header}
      {body}
    </div>
  );

  if (state.kind === "loading") {
    return framed(<p className="p-lg text-center text-muted">{COPY.teacher.loading}</p>);
  }
  if (state.kind === "failed") {
    return framed(
      <p className="p-lg text-center text-muted">
        {fill(COPY.teacher.failed, { message: state.message })}
      </p>,
    );
  }

  const { comparison } = state;
  const nameOf = (studentId: string) => {
    const student = comparison.students.find((s) => s.studentId === studentId);
    return [student?.firstName, student?.lastName].filter(Boolean).join(" ");
  };
  /** Family → its cell for one student, so the grid is a lookup rather than a scan per cell. */
  const cellAt = new Map(
    comparison.cells.map((c) => [`${c.studentId}|${c.family}`, c] as const),
  );

  if (comparison.games.length === 0) {
    return framed(
      <div className={`${CARD} text-center`}>
        <p className="text-body">{COPY.teacher.compare.empty}</p>
        <p className="mt-sm text-small text-muted">{COPY.teacher.compare.emptyHint}</p>
      </div>,
    );
  }

  return (
    <div className="mx-auto min-h-full max-w-[720px] px-lg pt-lg pb-xxl">
      {header}
      {/* The scope qualifier, on every load. */}
      <p className="-mt-sm mb-lg text-small text-muted">{COPY.teacher.compare.scopeNote}</p>

      {/*
        * Who needs a look, first.
        *
        * It was at the bottom, under every game's list — so the one thing on this screen a teacher
        * can act on tomorrow was the last thing they reached. Each row carries the game and both
        * numbers, so the question is answered without opening anything.
        */}
      <section className="mb-lg">
        <h2 className="mb-sm flex items-baseline justify-between gap-sm text-heading">
          <span>{COPY.teacher.compare.needsHelpHeading}</span>
          {comparison.needsHelp.length > 0 && (
            <span className="shrink-0 text-caption font-normal text-muted">
              {fill(COPY.teacher.compare.needsHelpCount, {
                count: fa(comparison.needsHelp.length),
              })}
            </span>
          )}
        </h2>
        {comparison.needsHelp.length === 0 ? (
          <p className={`${CARD} text-body text-muted`}>{COPY.teacher.compare.needsHelpEmpty}</p>
        ) : (
          <ul className="flex flex-col gap-sm">
            {comparison.needsHelp.map((entry) => {
              const game = comparison.games.find((g) => g.family === entry.family);
              return (
                <li key={`${entry.studentId}-${entry.family}`}>
                  <button
                    onClick={() => onOpenStudent(entry.studentId)}
                    className={`${CARD} w-full border-s-[3px] border-wrong text-start text-body`}
                  >
                    {fill(COPY.teacher.compare.needsHelpRow, {
                      name: nameOf(entry.studentId),
                      game: game ? taskLabel(game.taskId) : entry.family,
                      reached: fa(entry.reached),
                      median: fa(entry.median),
                    })}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/*
        * The class, as a grid.
        *
        * The name column is sticky and the games scroll under it, which is the one arrangement that
        * survives a class with more games than fit: a row stays readable as a child however far
        * across you are. Tapping a name opens the child; tapping a column opens that game's own
        * sorted list, which is what this whole screen used to be.
        */}
      <section className="mb-md">
        <h2 className="mb-sm text-heading">{COPY.teacher.compare.gridHeading}</h2>
        <div className={CARD}>
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 tabular-nums">
              <thead>
                <tr>
                  <th className="sticky start-0 bg-card" />
                  {comparison.games.map((game) => (
                    <th key={game.family} className="px-1 pb-sm align-bottom">
                      <button
                        className="text-caption font-bold text-muted"
                        onClick={() =>
                          setOpenGame(openGame === game.family ? null : game.family)
                        }
                      >
                        {taskShortLabel(game.taskId)}
                        <span className="block text-caption font-normal">
                          {game.median !== null && game.from >= MIN_STUDENTS_FOR_MEDIAN
                            ? fill(COPY.teacher.compare.gridMedian, { level: fa(game.median) })
                            : COPY.teacher.compare.gridNoMedian}
                        </span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.students.map((student, row) => (
                  <tr key={student.studentId}>
                    <th
                      className={`sticky start-0 bg-card pe-sm text-start text-small font-normal whitespace-nowrap ${
                        row > 0 ? "border-t border-edge" : ""
                      }`}
                    >
                      <button onClick={() => onOpenStudent(student.studentId)}>
                        {[student.firstName, student.lastName].filter(Boolean).join(" ")}
                      </button>
                    </th>
                    {comparison.games.map((game) => {
                      const cell = cellAt.get(`${student.studentId}|${game.family}`);
                      const standing = standingOf(cell, game.median, game.from);
                      return (
                        <td
                          key={game.family}
                          className={`px-1 ${row > 0 ? "border-t border-edge" : ""}`}
                        >
                          <span
                            className={`mx-auto my-1 grid size-[34px] place-items-center rounded-sm text-small font-bold ${CELL_LOOK[standing]}`}
                          >
                            {cell ? fa(cell.reached) : "–"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* A tinted cell makes a claim, so the claim is written down under it. */}
        <ul className="mt-sm flex flex-wrap gap-x-md gap-y-1 text-caption text-muted">
          <Key look="bg-correct/22" label={COPY.teacher.compare.keyAbove} />
          <Key look="bg-edge" label={COPY.teacher.compare.keyAt} />
          <Key look="bg-wrong/18" label={COPY.teacher.compare.keyBelow} />
          <Key look="border border-dashed border-edge" label={COPY.teacher.compare.keyThin} />
          <Key look="border border-edge" label={COPY.teacher.compare.keyNone} />
        </ul>
      </section>

      <p className="mb-lg text-caption text-muted">
        {fill(COPY.teacher.compare.floorNote, { min: fa(MIN_TRIALS_FOR_CLASS_COMPARISON) })}
      </p>

      {/*
        * One game's own list, on demand.
        *
        * Nothing was deleted when the grid arrived — this is the screen as it was, for one game, and
        * it is still the best way to read a single game top to bottom. It is simply no longer what a
        * teacher is given eight of before they have asked for any.
        */}
      {openGame &&
        (() => {
          const game = comparison.games.find((g) => g.family === openGame);
          if (!game) return null;
          const cells = comparison.cells
            .filter((c) => c.family === openGame)
            .sort((a, b) => b.reached - a.reached);
          return (
            <section className="mb-lg">
              <h2 className="mb-sm flex items-baseline justify-between gap-sm text-heading">
                <span>{taskLabel(game.taskId)}</span>
                <button
                  className="shrink-0 text-caption font-normal text-muted"
                  onClick={() => setOpenGame(null)}
                >
                  {COPY.teacher.compare.closeGame}
                </button>
              </h2>
              <ul className="flex flex-col gap-sm">
                {cells.map((cell) => {
                  const thin = cell.trials < MIN_TRIALS_FOR_CLASS_COMPARISON;
                  return (
                    <li key={cell.studentId}>
                      <button
                        onClick={() => onOpenStudent(cell.studentId)}
                        className={`${CARD} w-full text-start`}
                      >
                        <span className="flex items-baseline justify-between gap-sm">
                          <span className="text-body">{nameOf(cell.studentId)}</span>
                          <span className="shrink-0 text-caption text-muted tabular-nums">
                            {fill(COPY.teacher.student.level, {
                              reached: fa(cell.reached),
                              max: fa(game.max),
                            })}
                          </span>
                        </span>
                        <LevelBar reached={cell.reached} max={game.max} />
                        <span className="mt-1 block text-caption text-muted">
                          {/*
                            * A cell under the floor says so instead of showing an accuracy.
                            *
                            * The server sends `accuracy: null` there, and printing a number anyway
                            * would be inventing one from too few trials.
                            */}
                          {thin
                            ? COPY.teacher.compare.thinData
                            : fill(COPY.teacher.student.accuracy, {
                                correct: fa(Math.round((cell.accuracy ?? 0) * cell.trials)),
                                total: fa(cell.trials),
                              })}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })()}
    </div>
  );
}

function Key({ look, label }: { look: string; label: string }) {
  return (
    <li className="flex items-center gap-1">
      <i className={`inline-block size-[14px] rounded-xs ${look}`} />
      {label}
    </li>
  );
}
